import React, { useState } from "react";
import Table, { Row, Data } from "../../components/ui/table/table";

import { fetchCardList } from "../../api/card-service";

import { useQuery } from "@tanstack/react-query";
import { usePagination } from "../../hooks/usePagination";

import { formatAmount, getFormattedDate } from "../../utils/utils";
import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";
import { OnlyInPc, OnlyInMobile } from "../../components/Responsive";

import { Input, Select } from "../../components/form/formControls";
import { Button } from "../../components/ui/buttons/Button";
import { StatusBadge } from "../../components/StatusBadge";
import StatsCard from "../../components/cards/statsCard";

import { useDebounce } from "../../hooks/useDebounce";

import Pagination from "../../components/Pagination";

// Import Action Modals
import TransferFundDrawer from "./TransferFundDrawer";
import PrefundRequestModal from "./PrefundRequestModal";
import ViewTransferHistoryDrawer from "./ViewTransferHistoryDrawer";
import TransporterPaymentsDrawer from "./TransporterPaymentsDrawer";
import FeesLimitDrawer from "./FeesLimitDrawer";
import LoadLimitDrawer from "./LoadLimitDrawer";
import TeamBalanceDrawer from "./TeamBalanceDrawer";
import { useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { useLocale } from "../../hooks/useLocale";

// Assets
import CardStatsIcon1 from "../../static/icons/card_icon_1.png";
import CardStatsIcon2 from "../../static/icons/card_icon_2.png";
import CardStatsIcon3 from "../../static/icons/card_icon_3.png";
import { ActionMenu } from "../../components/ActionMeun";
import { fetchCardStats } from "../../api/wallet-service";
import { APP_USERS } from "../../consts/AppRoles";
import { OnlyFor, permitUser } from "../../components/Roles";
import {
  BLOCKED_FEATURES,
  isEmailBlockedForFeature,
} from "../../utils/blockedEmails";

const CardsPage = () => {
  const { localize } = useLocale(["cards"]);
  // globals
  const currentUser = useSelector((s) => s.auth.currentUser);
  const currentProgram = useSelector((s) => s.global.currentProgram);
  const inputDate = useSelector((s) => s.global.inputDate);

  // Drawers State
  const [isPrefundDrawaerOpen, setIsPrefundDrawaerOpen] = useState(false);
  const [isTransferFundOpen, setIsTransferFundOpen] = useState(false);
  const [isFeeLimitDrawerOpen, setIsFeeLimitDrawerOpen] = useState(false);
  const [isLoadLimitDrawerOpen, setIsLoadLimitDrawerOpen] = useState(false);
  const [isPaymentsDrawerOpen, setIsPaymentsDrawerOpen] = useState(false);
  const [isTeamBalanceDrawerOpen, setIsTeamBalanceDrawerOpen] = useState(false);
  const [isTransferHistoryDrawerOpen, setIsTransferHistoryDrawerOpen] =
    useState(false);

  // Current Selcted Item
  const [currentRecord, setCurrentRecord] = useState(null);
  const { page } = usePagination();

  // Handling Filters
  const filterSchema = {
    status: "",
    moxeyId: "",
    nameInEn: "",
    mobileNumber: "",
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);

  const cardList = useQuery(["cards/list", filters, inputDate, page], () =>
    fetchCardList({
      clientUserHashId: currentUser.clientHashId,
      dateRange: inputDate.date,
      page,
      filters,
    })
  );

  const pageStatQuery = useQuery(["cards/stats"], () =>
    fetchCardStats({
      userHashId: currentUser.userHashId,
      clientHashId: currentUser.clientHashId,
      programHashId: currentProgram,
      currencyCode: "AED",
    })
  );

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex justify-content-between">
            <h4>{localize("heading")}</h4>

            {/* Card Options on Desktop / Large Screen */}
            <OnlyInPc>
              <div className="">
                <button
                  type="button"
                  className="btn btn-light text-capitalize me-2 "
                  onClick={() => {
                    setIsFeeLimitDrawerOpen(true);
                  }}
                >
                  {localize("buttons.fees")}
                </button>
                <button
                  type="button"
                  className="btn btn-light text-capitalize me-2 "
                  onClick={() => {
                    setIsLoadLimitDrawerOpen(true);
                  }}
                >
                  {localize("buttons.limit")}
                </button>
                {!isEmailBlockedForFeature(
                  BLOCKED_FEATURES.CARDS.PAGE_HEADER_ACTIONS,
                  currentUser?.username
                ) && (
                  <>
                    <OnlyFor
                      roles={[
                        APP_USERS.CLIENT,
                        APP_USERS.SUPER_DISTRIBUTOR,
                        APP_USERS.DISTRIBUTOR,
                      ]}
                    >
                      <button
                        type="button"
                        className="btn btn-light text-capitalize me-2 "
                        onClick={() => {
                          setIsTeamBalanceDrawerOpen(true);
                        }}
                      >
                        {localize("buttons.teamBalance")}
                      </button>
                    </OnlyFor>
                    <button
                      type="button"
                      className="btn btn-light text-capitalize me-2 "
                      onClick={() => {
                        setIsPaymentsDrawerOpen(true);
                      }}
                    >
                      {localize("buttons.transporterPayments")}
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary text-capitalize text-light"
                      onClick={() => setIsPrefundDrawaerOpen(true)}
                    >
                      {localize("buttons.requestPrefund")}
                    </button>
                  </>
                )}
              </div>
            </OnlyInPc>

            {/* Card Options on Mobile Screen */}
            <OnlyInMobile>
              <div className="d-flex">
                <Button
                  text={localize("buttons.request")}
                  className="btn btn-primary text-light me-3"
                  onClick={() => setIsPrefundDrawaerOpen(true)}
                />

                <ActionMenu
                  name={localize("common.buttons.actions")}
                  options={[
                    {
                      name: localize("buttons.fees"),
                      onClick: () => {
                        setIsFeeLimitDrawerOpen(true);
                      },
                    },
                    {
                      name: localize("buttons.teamBalance"),
                      show: !permitUser(
                        [APP_USERS.AGENT],
                        currentUser.productType
                      ),
                      onClick: () => {
                        setIsTeamBalanceDrawerOpen(true);
                      },
                    },
                    {
                      name: localize("buttons.limit"),
                      onClick: () => {
                        setIsLoadLimitDrawerOpen(true);
                      },
                    },
                    {
                      name: localize("buttons.transporterPayments"),
                      onClick: () => {
                        setIsPaymentsDrawerOpen(true);
                      },
                    },
                  ]}
                />
              </div>
            </OnlyInMobile>
          </div>
        </Section>

        {/* Stats and Graphs */}
        <PageStatistics query={pageStatQuery} />

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-5">
              <h5 className="text-muted">{localize("table.name")}</h5>
            </div>

            {/* Table Data */}
            <Table
              loading={cardList.isLoading}
              cols={localize("table.cols", { returnObjects: true })}
              dataSize={cardList?.data?.body?.content.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {cardList?.data?.body?.content.map((item) => (
                <>
                  <Row>
                    <Data>
                      <StatusBadge status={item.cardStatus} />
                    </Data>
                    <Data>{getFormattedDate(item.createdAt)}</Data>
                    <Data>{item.moxeyId}</Data>
                    <Data>
                      {item.firstName} {item.lastName}
                    </Data>
                    <Data>{item.mobileNumber}</Data>
                    <Data>
                      <ActionMenu
                        name={localize("common.buttons.actions")}
                        options={[
                          {
                            name: localize("table.action.transferFund"),
                            show:
                              !isEmailBlockedForFeature(
                                BLOCKED_FEATURES.CARDS.TRANSFER_FUND,
                                currentUser?.username
                              ) &&
                              (item.cardStatus === "Active" ||
                                item.cardStatus === "Suspend"),
                            onClick: () => {
                              setCurrentRecord(item);
                              setIsTransferFundOpen(true);
                            },
                          },
                          {
                            name: localize("table.action.viewTransferHistory"),
                            show: true,
                            onClick: () => {
                              setCurrentRecord(item);
                              setIsTransferHistoryDrawerOpen(true);
                            },
                          },
                        ]}
                      />
                    </Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            {/* ======= Pagination =======*/}
            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={cardList?.data?.body?.content.length}
                totalPages={cardList?.data?.body?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>

        {/* =========== Action Modals =================*/}

        {/* View Request Prefund Action Modal*/}
        {isPrefundDrawaerOpen && (
          <PrefundRequestModal
            title={localize("requestPrefund.heading")}
            onClose={() => {
              setIsPrefundDrawaerOpen(false);
            }}
          />
        )}

        {/* Card Load Action Modal */}
        {isTransferFundOpen && (
          <TransferFundDrawer
            cardItem={currentRecord}
            title="Transfer Fund"
            onClose={() => {
              setIsTransferFundOpen(false);
            }}
          />
        )}

        {/* Transporter Payments Action Modal*/}
        {isPaymentsDrawerOpen && (
          <TransporterPaymentsDrawer
            cardItem={currentRecord}
            title={localize("transporterPayments.heading")}
            onClose={() => {
              setIsPaymentsDrawerOpen(false);
            }}
          />
        )}

        {/* View Transfer History Action Modal */}
        {isTransferHistoryDrawerOpen && (
          <ViewTransferHistoryDrawer
            cardItem={currentRecord}
            title={localize("transferHistory.heading")}
            onClose={() => {
              setIsTransferHistoryDrawerOpen(false);
            }}
          />
        )}

        {/* View Fees Limit Action Modal */}
        {isFeeLimitDrawerOpen && (
          <FeesLimitDrawer
            title={localize("fees.heading")}
            onClose={() => {
              setIsFeeLimitDrawerOpen(false);
            }}
          />
        )}

        {/* View Limit Action Modal */}
        {isLoadLimitDrawerOpen && (
          <LoadLimitDrawer
            title={localize("limit.heading")}
            onClose={() => {
              setIsLoadLimitDrawerOpen(false);
            }}
          />
        )}

        {/* View Load Limit Action Modal */}
        {isTeamBalanceDrawerOpen && (
          <TeamBalanceDrawer
            title={localize("teamBalance.heading")}
            onClose={() => {
              setIsTeamBalanceDrawerOpen(false);
              pageStatQuery.refetch();
            }}
          />
        )}
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  const statusOptions = [
    {
      value: "All",
      text: "All",
    },
    {
      value: "Active",
      text: "Active",
    },
    {
      value: "Inactive",
      text: "Inactive",
    },
    {
      value: "Expired",
      text: "Expired",
    },
    {
      value: "Closed",
      text: "Closed",
    },
    {
      value: "Restricted",
      text: "Restricted",
    },
    {
      value: "Temporary_Block",
      text: "Temporary Block",
    },
    {
      value: "Permenent_Block",
      text: "Permanent Block",
    },
    {
      value: "PERMANENT_BLOCK_REQUEST",
      text: "Permanent Block Request",
    },
    {
      value: "TEMPORARY_BLOCK_REQUEST",
      text: "Temporary Block Request",
    },
    {
      value: "Close_Card_Request",
      text: "Close Card Request",
    },
  ];
  return (
    <>
      <tr>
        <td>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => {
              handleFilteredValues(event);
            }}
          >
            <option value="" selected>
              Select status
            </option>
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </td>
        <td />
        <td>
          <Input
            name="moxeyId"
            value={filteredValues.moxeyId}
            onInput={(event) => {
              handleFilteredValues(event);
            }}
          />
        </td>
        <td>
          <Input
            name="nameInEn"
            value={filteredValues.nameInEn}
            onInput={(event) => {
              handleFilteredValues(event);
            }}
          />
        </td>
        <td>
          <Input
            name="mobileNumber"
            value={filteredValues.mobileNumber}
            onInput={(event) => {
              handleFilteredValues(event);
              handleFilter(filteredValues);
            }}
          />
        </td>
        <td />
      </tr>
    </>
  );
};

const PageStatistics = ({ query }) => {
  const currentUser = useSelector((s) => s.auth.currentUser);
  const { localize } = useLocale(["cards"]);

  return (
    <>
      {query.isLoading ? (
        <>
          <Section>
            <div className="row gy-3">
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
            </div>
          </Section>
        </>
      ) : (
        <>
          <Section>
            <div className="row gy-3">
              <div className="col-sm-12 col-md-6 col-lg-3">
                <StatsCard
                  image={CardStatsIcon1}
                  text={localize("stats.availablePrefund")}
                  value={formatAmount(query?.data?.balance)}
                  valueLabel="AED"
                />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <StatsCard
                  image={CardStatsIcon1}
                  text={localize("stats.totalPrefundBalance")}
                  value={formatAmount(query?.data?.totalBalance)}
                  valueLabel="AED"
                />
              </div>

              <div className="col-sm-12 col-md-6 col-lg-3">
                <StatsCard
                  image={CardStatsIcon3}
                  text={localize("stats.prefundOnHold")}
                  value={formatAmount(query?.data?.holdBalance)}
                  valueLabel="AED"
                />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <StatsCard
                  image={CardStatsIcon2}
                  text={localize("stats.totalActiveCards")}
                  value={query?.data?.totalActiveCardCount}
                />
              </div>
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default CardsPage;
